import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";

import { setAuthCookies } from "../../lib/cookie";

const EAGER_EXPIRY_SECS = 30;

const isNetworkError = (e: unknown) => {
  if (
    e instanceof TypeError &&
    [
      "The network connection was lost.", // Safari
      "Failed to fetch", // Chrome
      "NetworkError when attempting to fetch resource.", // Firefox
      "Network request failed", // polyfill from https://github.com/github/fetch
    ].includes(e.message)
  ) {
    return true;
  }
  return false;
};

export interface Auth0SessionRefresherProps {
  children?: React.ReactNode;
}

function getExpiryFromIdToken(idToken: string): number {
  const decoded = jwtDecode<{ exp?: number }>(idToken);
  if (!decoded.exp) {
    throw new Error("ID token has no expiry.");
  }

  return (decoded.exp * 1000 - Date.now()) / 1000;
}

export const Auth0SessionRefresher = ({ children }: Auth0SessionRefresherProps): JSX.Element => {
  const auth0 = useAuth0();

  const [error, setError] = useState<Error | null>(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  const [online, setOnline] = useState(navigator.onLine);
  const onNetworkStatusChange = () => {
    setOnline(navigator.onLine);
  };

  const [documentVisible, setDocumentVisible] = useState(!document.hidden);
  const onDocumentVisibleChange = () => {
    setDocumentVisible(!document.hidden);
  };

  useEffect(() => {
    if (typeof window !== "undefined" && window.addEventListener) {
      window.addEventListener("online", onNetworkStatusChange);
      window.addEventListener("offline", onNetworkStatusChange);
      document.addEventListener("visibilitychange", onDocumentVisibleChange);

      return () => {
        window.removeEventListener("online", onNetworkStatusChange);
        window.removeEventListener("offline", onNetworkStatusChange);
        document.removeEventListener("visibilitychange", onDocumentVisibleChange);
      };
    }
  }, []);

  /**
   * Force refresh the access token and ID token, updating the cookies.
   */
  const refreshTokens = useCallback(async () => {
    try {
      const tokens = await auth0.getAccessTokenSilently({
        detailedResponse: true,
        cacheMode: "off",
      });

      setAuthCookies({
        accessToken: tokens.access_token,
        idToken: tokens.id_token,
      });
    } catch (e: any) {
      setError(e);
      return;
    }
  }, [auth0]);

  /**
   * Get the minimum expiry time between the access token and ID token for the
   * current set of tokens.
   */
  const getMinExpiresIn = useCallback(async () => {
    try {
      const tokens = await auth0.getAccessTokenSilently({
        detailedResponse: true,
        cacheMode: "cache-only",
      });

      // With "cache-only" mode, getAccessTokenSilently returns undefined if no
      // tokens are in the cache. It might still be possible to fetch new tokens
      // though.
      if (tokens === undefined) {
        return 0;
      }

      const accessTokenExpiresInSecs = tokens.expires_in;
      const idTokenExpiresInSecs = getExpiryFromIdToken(tokens.id_token);

      return Math.min(accessTokenExpiresInSecs, idTokenExpiresInSecs);
    } catch (e: any) {
      setError(e);
      return undefined;
    }
  }, [auth0]);

  const refreshTimer = useCallback(async () => {
    if (online) {
      try {
        await refreshTokens();
        setLastRefresh(Date.now());
      } catch (err: any) {
        setError(err);
      }
    }
  }, [online, refreshTokens, setLastRefresh, setError]);

  useEffect(() => {
    if (error) {
      if (isNetworkError(error)) {
        return;
      }
      // eslint-disable-next-line no-console
      console.error("Session refresh error", error);
      return;
    } else if (auth0.error) {
      if (isNetworkError(auth0.error)) {
        return;
      }
      // eslint-disable-next-line no-console
      console.error("Session refresh Auth0 error", auth0.error);
      return;
    } else if (auth0.isLoading) {
      return;
    }

    const promise = (async () => {
      if (!online) {
        return;
      }

      const minExpiresIn = await getMinExpiresIn();
      if (minExpiresIn === undefined) {
        return;
      }

      const timerHandle = setTimeout(refreshTimer, (minExpiresIn - EAGER_EXPIRY_SECS) * 1000);
      return timerHandle;
    })();

    promise.catch((err) => {
      setError(err);
    });

    return () => {
      promise.then((timerHandle) => {
        if (timerHandle) {
          clearTimeout(timerHandle);
        }
      });
    };
  }, [auth0, documentVisible, error, lastRefresh, online, getMinExpiresIn, refreshTimer]);

  return <>{children}</>;
};
